import React, { useState } from "react"
import PropTypes from "prop-types"
import { Form, Checkbox, Radio, Space, Typography, Card, Flex } from "antd"
import { useTranslation, Trans } from "react-i18next"
import { ExclamationCircleOutlined } from "@ant-design/icons"
import { useDispatch } from "react-redux"
import { setClientId } from "store/bookings"
import styles from "./BookingForm.module.css"

const { Text } = Typography

const buildPhoneValue = (client) =>
  client?.phone_number ? { fullNumber: client.phone_number, countryCode: client.country_code } : null

const applyClientToForm = (form, client, isExistingClient, dispatch) => {
  const phoneValue = buildPhoneValue(client)
  form.setFieldsValue({
    email: client?.email,
    phone_number: phoneValue,
    existing_client: { label: client?.full_name, value: client?.user_id, phone_number: phoneValue },
    isExistingClient
  })
  dispatch(setClientId(client?.user_id))
}

const AlreadyLinkedNotice = ({ form, singleMatchedClient, singleMatchType, conflictClients, onClientSelected }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [selectedConflictId, setSelectedConflictId] = useState(null)

  const handleSingleClientSelect = (e) => {
    const checked = e.target.checked
    if (checked && singleMatchedClient) {
      applyClientToForm(form, singleMatchedClient, checked, dispatch)
      onClientSelected()
    }
  }

  const handleConflictSelect = (e) => {
    const userId = e.target.value
    setSelectedConflictId(userId)
    const client = conflictClients.find((c) => c.user_id === userId)
    if (client) {
      applyClientToForm(form, client, true, dispatch)
      onClientSelected()
    }
  }

  if (conflictClients) {
    return (
      <Card className={styles.alreadyLinkError}>
        <Space direction="vertical" size={12}>
          <Space>
            <ExclamationCircleOutlined className={styles.errorIcon} />
            <Text>
              <Trans
                i18nKey="booking.conflict_client"
                values={{ emailName: conflictClients[0].full_name, phoneName: conflictClients[1].full_name }}
                components={{ b: <b /> }}
              />
            </Text>
          </Space>
          <Radio.Group value={selectedConflictId} onChange={handleConflictSelect}>
            <Space direction="vertical" size={8}>
              {conflictClients.map((client) => (
                <Radio key={client.user_id} value={client.user_id}>
                  <Flex vertical gap={2}>
                    <Text strong>{client.full_name}</Text>
                    <Space size={16}>
                      {client.email && <Text type="secondary">{client.email}</Text>}
                      {client.phone_number && <Text type="secondary">{client.phone_number}</Text>}
                    </Space>
                  </Flex>
                </Radio>
              ))}
            </Space>
          </Radio.Group>
        </Space>
      </Card>
    )
  }

  const message = singleMatchType === "both"
    ? t("booking.email_and_phone_already_exists")
    : singleMatchType === "email"
    ? t("booking.email_already_exists")
    : t("booking.phone_already_exists")

  return (
    <Card className={styles.alreadyLinkError}>
      <Space direction="vertical" size={8}>
        <Space>
          <ExclamationCircleOutlined className={styles.errorIcon} />
          <Text>{message}</Text>
        </Space>
        <Form.Item name="isExistingClient" valuePropName="checked">
          <Checkbox onChange={handleSingleClientSelect}>{t("booking.continue_with_existing_client")}</Checkbox>
        </Form.Item>
      </Space>
    </Card>
  )
}

AlreadyLinkedNotice.propTypes = {
  form: PropTypes.object.isRequired,
  singleMatchedClient: PropTypes.object,
  singleMatchType: PropTypes.oneOf(["email", "phone", "both"]),
  conflictClients: PropTypes.arrayOf(PropTypes.object),
  onClientSelected: PropTypes.func.isRequired
}

export default AlreadyLinkedNotice
